import { useState, useEffect, useRef } from "react";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import { useDashboardSignalR } from "@/utils/useDashboardSignalR";
import TestEditModal from "@modals/TestEditModal";
import DownloadDataModal, { type DownloadDataOptions } from "@modals/DownloadDataModal";
import { GoGear } from "react-icons/go";
import { FaFilter, FaFileExcel } from "react-icons/fa";
import TestDetailsModal from "@modals/TestDetailsModal";
import RecentsRow from "@elements/RecentsRow";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import DateTimePicker from "@elements/DateTimePicker";
import FilterValueSelect from "@elements/FilterValueSelect";
import FilterTextInput from "@elements/FilterTextInput";
import { useI18n } from "@/i18n/I18nProvider";
import { visibleCodeExpiration } from "@/utils/testTimeoutDisplayHelpers";
import { prefetchWhenIdle } from "@/utils/runWhenIdle";
import { buildVisibleRecentsColumns, resolveRecentsColumnView } from "@/utils/recentsColumnHelpers";
import {
    downloadAuthenticatedFile,
    downloadAuthenticatedFilesToDirectory,
    downloadResponseAsFile,
    triggerBrowserDownload,
} from "@/utils/authenticatedFileDownload";
import { apiMessage } from "@/utils/apiMessage";
import {
    applyDateFilterUiOperator,
    dateFilterNeedsPicker,
    extractDateFilterInput,
    getDateFilterUiOperator,
    parseDateFilterValue,
    serializeDateFilter,
} from "@/utils/recentsDateFilter";
import { isTextFilterColumn } from "@/utils/recentsFilterOptions";

import {
    HiOutlineChevronRight,
    HiOutlineChevronLeft,
    HiOutlineChevronDoubleRight,
    HiOutlineChevronDoubleLeft,
} from "react-icons/hi";

function safeReadCurrentUserFromSession() {
    try {
        const raw = window.sessionStorage.getItem("User");
        if (!raw) return null;
        return JSON.parse(atob(raw));
    } catch {
        return null;
    }
}

const SORT_ONLY_COLUMNS: string[] = [];
const EMPTY_NOT_EMPTY_ONLY_COLUMNS = ['ApplicantScore', 'CompanyScore', 'OverallScore', 'Duration'];
const SCORE_COLUMN_NAMES = ['ApplicantScore', 'CompanyScore', 'OverallScore'];

function columnsNeedScores(cols) {
    return Array.isArray(cols) && cols.some((col) => SCORE_COLUMN_NAMES.includes(col.name));
}

function getColumnDataType(column) {
    return column?.dataType ?? column?.DataType ?? '';
}

function isDateColumn(column) {
    return getColumnDataType(column).toLowerCase() === 'datetime';
}

function isSortOnlyColumn(columnOrName) {
    const name = typeof columnOrName === 'string' ? columnOrName : columnOrName?.name;
    return SORT_ONLY_COLUMNS.includes(name);
}

function isEmptyNotEmptyOnlyColumn(column) {
    if (!column) return false;
    if (isDateColumn(column)) return false;
    return EMPTY_NOT_EMPTY_ONLY_COLUMNS.includes(column.name);
}

export default function RecentsTable(props) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [data, updateData] = useState<any>({});
    const [checked, updateChecks] = useState<any[]>([]);
    const [statuses, UpdateStatuses] = useState<any[]>([]);
    const [isAdmin, UpdateAdmin] = useState(false);
    const [filters, setFilters] = useState<any>({}); // Store filter values with structure: { columnName: { order: "ASC"|"DESC", options: [], value: "" } }
    const [filterOrder, setFilterOrder] = useState<string[]>([]); // Store the order of filters for drag-and-drop
    const [recentsData, updateRecentsData] = useState<any>(null);
    const [pageNumber, updatePageNumber] = useState(1);
    const [columns, updateColumns] = useState<any[]>([]);
    const [simRoleId, updateSimRoleId] = useState<any>("");
    const [detailsTestInstanceId, updateDetailsTestInstanceId] = useState(null);
    const [detailsActiveTab, updateDetailsActiveTab] = useState("details");
    const [company, updateCompany] = useState<any>(null);
    const [pageSize, updatePageSize] = useState(() => {
        const storedValue = localStorage.getItem('perPage');
        return storedValue && !isNaN(parseInt(storedValue)) ? parseInt(storedValue) : 15;
    });
    const [totalCount, updateTotalCount] = useState(0);
    const { t } = useI18n();
    const [canViewReports, setCanViewReports] = useState(false);
    const [canViewMatches, setCanViewMatches] = useState(false);
    const [canToggleArchived, setCanToggleArchived] = useState(false);
    const loadingOptionsRef = useRef(new Set()); // Track which fields are currently loading options

    const [pageSizeStr, updatePageSizeStr] = useState(() => {
        return localStorage.getItem('perPage') || "15";
    });
    let totalPages = useRef(0);
    let lastUrl = useRef("");
    let skipFilterOrderFetch = useRef(true);
    let skipPaginationFetch = useRef(true);
    let skipFiltersFetch = useRef(true);
    let scoresRequested = useRef(false);
    let navigate = useNavigate();

    const companyId = props?.tabData?.company?.id;

    // Reload recents state when the active company changes.
    useEffect(() => {
        const isAdminUser = Array.isArray(props.tabData?.adminPerms)
            ? props.tabData.adminPerms.some((perm) => {
                const permValue = typeof perm === "number" ? perm : (perm?.value ?? perm?.permissionId ?? perm?.permId);
                return Number(permValue) === Enums.ROLE_PERMS.SystemAdmin;
            })
            : false;
        UpdateAdmin(isAdminUser);

        if (!companyId) return;

        lastUrl.current = "";
        updatePageNumber(1);
        updateData({});
        updateRecentsData(null);
        skipPaginationFetch.current = true;
        skipFiltersFetch.current = true;
        scoresRequested.current = false;

        if (props.tabData?.company) {
            updateCompany(props.tabData.company);
        }

        loadRecentsTabAsync();
    }, [companyId]);

    useEffect(() => {
        if (skipPaginationFetch.current) {
            skipPaginationFetch.current = false;
            return;
        }
        getTestsAsync();
    }, [pageNumber, pageSize]);

    useEffect(() => {
        Object.entries(filters as any).forEach(([fieldName, filterData]: [string, any]) => {
            if (filterData.hasFilter &&
                !filterData.optionsLoading &&
                (!filterData.options || filterData.options.length === 0) &&
                !loadingOptionsRef.current.has(fieldName)) {
                const column = columns.find(col => col.name === fieldName);
                if (isDateColumn(column) || isSortOnlyColumn(fieldName) || isTextFilterColumn(fieldName)) {
                    return;
                }
                loadingOptionsRef.current.add(fieldName);
                getFilterOptionsAsync(fieldName).finally(() => {
                    loadingOptionsRef.current.delete(fieldName);
                });
            }
        });
        if (skipFiltersFetch.current) {
            skipFiltersFetch.current = false;
            return;
        }
        getTestsAsync();
    }, [filters]);

    useEffect(() => {
        if (skipFilterOrderFetch.current) {
            skipFilterOrderFetch.current = false;
            return;
        }
        if (company?.id && filterOrder.length > 0) {
            lastUrl.current = "";
            getTestsAsync(true);
        }
    }, [filterOrder]);

    useEffect(() => {
        if (props?.tabData?.company) {

            if (props?.tabData?.company?.id) {
                window.sessionStorage.setItem("CompanyId", props.tabData.company.id);
            }

            const hasCompanySystemAdmin = !!props?.tabData?.company?.permissions?.find((cp) => {
                const permValue = typeof cp === "number" ? cp : (cp?.value ?? cp?.permissionId ?? cp?.permId);
                return Number(permValue) === Enums.ROLE_PERMS.SystemAdmin;
            });
            const hasAdminSystemAdmin = Array.isArray(props.tabData?.adminPerms)
                && props.tabData.adminPerms.some((perm) => {
                    const permValue = typeof perm === "number" ? perm : (perm?.value ?? perm?.permissionId ?? perm?.permId);
                    return Number(permValue) === Enums.ROLE_PERMS.SystemAdmin;
                });
            UpdateAdmin(hasCompanySystemAdmin || hasAdminSystemAdmin);
            UpdateStatuses(props?.tabData?.company?.statuses || []);
        }
    }, [props?.tabData?.company, simRoleId]);

    useEffect(() => {
        DetermineShownColumns();
    }, [recentsData, simRoleId, isAdmin]);

    useEffect(() => {
        if (!columnsNeedScores(columns) || scoresRequested.current) return;
        if (!data?.tests?.length) return;
        scoresRequested.current = true;
        getTestsAsync(true, true);
    }, [columns, data?.tests]);

    function formatApplicantName(test) {
        const firstName = String(test?.firstName || "").trim();
        const lastName = String(test?.lastName || "").trim();
        return [firstName, lastName].filter(Boolean).join(" ");
    }

    function findApplicantNameForToast(eventData, tests) {
        if (!Array.isArray(tests) || tests.length === 0) return "";

        const matchingTest = tests.find((test) => {
            const matchesTestId = eventData?.testId && Number(test?.testId || 0) === Number(eventData.testId);
            const matchesInstanceId = eventData?.testInstanceId && Number(test?.testInstanceId || 0) === Number(eventData.testInstanceId);
            return matchesTestId || matchesInstanceId;
        });

        return formatApplicantName(matchingTest);
    }

    useDashboardSignalR(async (eventData) => {
        const relevantEvents = ["testStarted", "testCreated", "testCompleted", "testGraded"];
        if (!relevantEvents.includes(eventData?.eventType ?? "")) return;
        if (!company?.id) return;
        if (Number(eventData?.companyId || 0) !== Number(company.id)) return;

        const refreshedData = await getTestsAsync(true);
        const applicantName = findApplicantNameForToast(eventData, refreshedData?.tests) || findApplicantNameForToast(eventData, data?.tests);
        const personLabel = applicantName ? ` for ${applicantName}` : "";
        const testLabel = eventData?.testId ? ` (Test #${eventData.testId})` : "";

        if (eventData?.eventType === "testStarted") toast.info(`Test started${personLabel}${testLabel}`);
        else if (eventData?.eventType === "testCreated") toast.info(`New test created${personLabel}${testLabel}`);
        else if (eventData?.eventType === "testCompleted") toast.info(`Test completed${personLabel}${testLabel}`);
        else if (eventData?.eventType === "testGraded") toast.success(`Test graded${personLabel}${testLabel}`);
    });

    // Early return checks after all hooks are declared (Rules of Hooks compliance)
    if (!props || !props.tabData) {
        return null;
    }

    function showTestDetails(testInstanceId) {
        updateDetailsTestInstanceId(testInstanceId);
        updateDetailsActiveTab("details");
    }

    function showTestScores(testInstanceId) {
        updateDetailsTestInstanceId(testInstanceId);
        updateDetailsActiveTab("scores");
    }
    function showTestMatches(testInstanceId) {
        updateDetailsTestInstanceId(testInstanceId);
        updateDetailsActiveTab("matches");
    }

    async function loadRecentsTabAsync() {
        const resolvedCompanyId = getCompanyId();
        if (!resolvedCompanyId) return;

        const loadStartedAt = performance.now();
        props.tabData.updateSpinner(true);
        try {
            const [listResp, testsResp] = await Promise.all([
                RightTimeApi.getAsync("/Company/" + resolvedCompanyId + "/RecentsHeader/list"),
                fetchTestsPageAsync(resolvedCompanyId, true, false),
            ]);

            if (listResp.ok && listResp.jsonData) {
                updateRecentsData(listResp.jsonData);
                updateSimRoleId(String(listResp.jsonData.defaultRoleId ?? ""));
            }

            if (testsResp && import.meta.env.DEV) {
                console.info(`[perf] Recents list visible in ${Math.round(performance.now() - loadStartedAt)}ms`);
            }

            prefetchWhenIdle(() => loadRecentsContextAsync(resolvedCompanyId));
        } finally {
            props.tabData.updateSpinner(false);
        }
    }

    async function loadRecentsContextAsync(resolvedCompanyId) {
        const companyIdToLoad = resolvedCompanyId ?? getCompanyId();
        if (!companyIdToLoad) return;

        const resp = await RightTimeApi.getAsync("/Company/" + companyIdToLoad + "/RecentsHeader/context");
        if (resp.ok && resp.jsonData) {
            updateRecentsData((prev) => ({ ...(prev ?? {}), ...resp.jsonData }));
            if (import.meta.env.DEV) {
                console.info("[perf] Recents context loaded");
            }
        }
    }

    function appendLimitToUserSitesParam(params: Record<string, string | number>) {
        if (props.limitToUserSites) {
            params.limitToUserSites = "true";
        }
    }

    async function fetchTestsPageAsync(resolvedCompanyId, refresh = false, includeScores = null) {
        let params: Record<string, string | number> = {
            page: pageNumber,
            perPage: pageSize
        };
        appendLimitToUserSitesParam(params);
        const shouldIncludeScores = includeScores ?? columnsNeedScores(columns);
        if (!shouldIncludeScores) {
            params.includeScores = "false";
        }
        if (isAdmin && Array.isArray(columns) && columns.some((c) => c.name === "Archived")) {
            params.includeArchived = "true";
        }
        const sortColumns: Record<string, string> = {};
        filterOrder.forEach(columnName => {
            if (filters[columnName] && filters[columnName].order && filters[columnName].order !== "FILTER_ONLY") {
                sortColumns[columnName] = filters[columnName].order;
            }
        });

        if (Object.keys(sortColumns).length > 0) {
            params.sortColumns = JSON.stringify(sortColumns);
        }

        const fieldFilters = Object.entries(filters as Record<string, { value?: string }>)
            .filter(([_, filterData]) => filterData.value)
            .reduce((acc, [field, filterData]) => {
                acc[field] = filterData.value;
                return acc;
            }, {} as Record<string, string>);

        if (Object.keys(fieldFilters).length > 0) {
            params.fieldFilters = JSON.stringify(fieldFilters);
        }

        const urlParams = new URLSearchParams(
            Object.entries(params).reduce((acc, [k, v]) => {
                acc[k] = String(v);
                return acc;
            }, {} as Record<string, string>)
        ).toString();
        const fullUrl = `/Tests/${resolvedCompanyId}?${urlParams}`;
        if (fullUrl === lastUrl.current && !refresh) {
            return null;
        }

        lastUrl.current = fullUrl;
        const resp = await RightTimeApi.getAsync(RightTimeApi.URL(fullUrl));
        if (resp.ok && resp.jsonData) {
            updateData(resp.jsonData);
            updateTotalCount(resp.jsonData.total || 0);
            totalPages.current = Math.trunc(resp.jsonData.total / pageSize) + 1;
            return resp.jsonData;
        }

        if (resp && resp.statusCode === 401) {
            navigate("/");
        }
        return null;
    }

    async function onCloseTestEditModalAsync() {
        setIsModalOpen(false);
        updateChecks([]);
        // Force refresh by clearing the lastUrl cache to ensure data is reloaded
        lastUrl.current = "";
        await getTestsAsync();
    } async function clickColumnTitleAsync(column) {
        updatePageNumber(1);
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters };

            if (newFilters[column.name]) {
                const currentOrder = newFilters[column.name].order;
                const isSortOnly = isSortOnlyColumn(column);
                const canHaveFilters = !isSortOnly;

                let newOrder;
                if (isSortOnly) {
                    newOrder = currentOrder === "ASC" ? "DESC" : "ASC";
                } else if (canHaveFilters) {
                    if (currentOrder === "ASC") {
                        newOrder = "DESC";
                    } else if (currentOrder === "DESC") {
                        newOrder = "FILTER_ONLY";
                    } else {
                        newOrder = "ASC";
                    }
                } else {
                    newOrder = currentOrder === "ASC" ? "DESC" : "ASC";
                }
                newFilters[column.name] = {
                    ...newFilters[column.name],
                    order: newOrder
                };

                if (newOrder === "FILTER_ONLY" && !isDateColumn(column) && !isTextFilterColumn(column) &&
                    (!newFilters[column.name].options || newFilters[column.name].options.length === 0)) {
                    setTimeout(() => getFilterOptionsAsync(column.name), 0);
                }

            } else {
                newFilters[column.name] = {
                    order: "ASC",
                    options: [],
                    value: "",
                    hasFilter: false,
                    optionsLoading: false
                };

                setFilterOrder(prevOrder => {
                    if (!prevOrder.includes(column.name)) {
                        return [...prevOrder, column.name];
                    }
                    return prevOrder;
                });
            }

            return newFilters;
        });
    }
    // Remove a specific sort/filter criterion
    function removeColumnFilter(columnName) {
        setFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            delete newFilters[columnName];
            return newFilters;
        });

        setFilterOrder(prevOrder => prevOrder.filter(name => name !== columnName));
        updatePageNumber(1);
    }

    function getCompanyId() {
        return company?.id ?? props?.tabData?.company?.id ?? null;
    }

    function disableColumnFilter(columnName) {
        setFilters(prevFilters => {
            if (!prevFilters[columnName]) return prevFilters;
            return {
                ...prevFilters,
                [columnName]: {
                    ...prevFilters[columnName],
                    hasFilter: false,
                    optionsLoading: false,
                    options: [],
                    value: ""
                }
            };
        });
    }

    async function enableColumnFilter(columnName) {
        const column = columns.find(col => col.name === columnName);
        if (isSortOnlyColumn(columnName)) return;

        if (isTextFilterColumn(columnName)) {
            setFilters(prevFilters => ({
                ...prevFilters,
                [columnName]: {
                    ...prevFilters[columnName],
                    hasFilter: true,
                    optionsLoading: false,
                    options: []
                }
            }));
            return;
        }

        const presetOptions = isEmptyNotEmptyOnlyColumn(column)
            ? ['[Empty]', '[Not Empty]']
            : [];

        setFilters(prevFilters => ({
            ...prevFilters,
            [columnName]: {
                ...prevFilters[columnName],
                hasFilter: true,
                optionsLoading: presetOptions.length === 0 && !isDateColumn(column),
                options: presetOptions.length > 0
                    ? presetOptions
                    : (prevFilters[columnName]?.options || [])
            }
        }));

        if (isDateColumn(column) || presetOptions.length > 0) return;

        if (loadingOptionsRef.current.has(columnName)) return;
        loadingOptionsRef.current.add(columnName);
        try {
            await getFilterOptionsAsync(columnName);
        } finally {
            loadingOptionsRef.current.delete(columnName);
        }
    }

    function removeFilterFromSort(columnName) {
        disableColumnFilter(columnName);
    }  // Toggle sort direction for a specific column
    function toggleSortDirection(columnName) {
        setFilters(prevFilters => {
            const currentFilter = prevFilters[columnName];
            const column = columns.find(col => col.name === columnName);
            const isSortOnly = isSortOnlyColumn(columnName);
            const hasFilterOptions = currentFilter.options && currentFilter.options.length > 0;
            const isDate = isDateColumn(column);
            const isTextFilter = isTextFilterColumn(columnName);

            let newOrder;

            if (isSortOnly || (!hasFilterOptions && !isDate && !isTextFilter)) {
                newOrder = currentFilter.order === "ASC" ? "DESC" : "ASC";
            } else {
                if (currentFilter.order === "ASC") {
                    newOrder = "DESC";
                } else if (currentFilter.order === "DESC") {
                    newOrder = "FILTER_ONLY";
                } else {
                    newOrder = "ASC";
                }
            }

            return {
                ...prevFilters,
                [columnName]: {
                    ...currentFilter,
                    order: newOrder
                }
            };
        });
        updatePageNumber(1);
    }
    // Handle drag start for filter reordering
    function handleFilterDragStart(event, columnName) {
        event.dataTransfer.setData("text/plain", columnName);
        event.dataTransfer.effectAllowed = "move";
        event.target.classList.add("dragging");
    }

    // Handle drag end for filter reordering
    function handleFilterDragEnd(event) {
        event.target.classList.remove("dragging");
    }

    // Handle drag over for filter reordering
    function handleFilterDragOver(event) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    }

    // Handle drop for filter reordering
    function handleFilterDrop(event, targetColumnName) {
        event.preventDefault();
        const draggedColumnName = event.dataTransfer.getData("text/plain");

        if (draggedColumnName !== targetColumnName) {
            setFilterOrder(prevOrder => {
                const newOrder = [...prevOrder];
                const draggedIndex = newOrder.indexOf(draggedColumnName);
                const targetIndex = newOrder.indexOf(targetColumnName);

                newOrder.splice(draggedIndex, 1);
                newOrder.splice(targetIndex, 0, draggedColumnName);
                return newOrder;
            });
            lastUrl.current = "";
            updatePageNumber(1);
        }
    }

    async function getFilterOptionsAsync(field) {
        try {
            const column = columns.find(col => col.name === field);

            if (isSortOnlyColumn(field)) {
                setFilters(prevFilters => ({
                    ...prevFilters,
                    [field]: {
                        ...prevFilters[field],
                        options: [],
                        optionsLoading: false
                    }
                }));
                return;
            }

            if (isTextFilterColumn(field)) {
                setFilters(prevFilters => ({
                    ...prevFilters,
                    [field]: {
                        ...prevFilters[field],
                        options: [],
                        optionsLoading: false
                    }
                }));
                return;
            }

            if (isDateColumn(column)) {
                setFilters(prevFilters => ({
                    ...prevFilters,
                    [field]: {
                        ...prevFilters[field],
                        optionsLoading: false
                    }
                }));
                return;
            }

            if (isEmptyNotEmptyOnlyColumn(column)) {
                setFilters(prevFilters => ({
                    ...prevFilters,
                    [field]: {
                        ...prevFilters[field],
                        options: ['[Empty]', '[Not Empty]'],
                        optionsLoading: false
                    }
                }));
                return;
            }

            const companyId = getCompanyId();
            if (!companyId) {
                setFilters(prevFilters => ({
                    ...prevFilters,
                    [field]: {
                        ...prevFilters[field],
                        options: ['[Empty]', '[Not Empty]'],
                        optionsLoading: false
                    }
                }));
                return;
            }

            let url = `/Tests/${companyId}/Field/${field}`;

            const activeFilters = Object.entries(filters as any)
                .filter(([_, filterData]: [string, any]) => filterData.value)
                .reduce((acc, [fieldName, filterData]: [string, any]) => {
                    acc[fieldName] = filterData.value;
                    return acc;
                }, {} as Record<string, any>);

            const queryParams = new URLSearchParams();
            if (props.limitToUserSites) {
                queryParams.append("limitToUserSites", "true");
            }
            if (Object.keys(activeFilters).length > 0) {
                Object.entries(activeFilters).forEach(([fieldName, value]) => {
                    queryParams.append('filterField', fieldName);
                    queryParams.append('filterValue', value);
                });
            }
            if (queryParams.toString()) {
                url += `?${queryParams.toString()}`;
            }

            props.tabData.updateSpinner(true);
            const resp = await RightTimeApi.getAsync(url);
            props.tabData.updateSpinner(false);

            let enhancedOptions = ['[Empty]', '[Not Empty]'];
            if (resp.ok && Array.isArray(resp.jsonData)) {
                enhancedOptions = ['[Empty]', '[Not Empty]', ...resp.jsonData];
            } else {
                console.error(`API call failed for ${field}:`, resp);
                toast.error(`Could not load filter values for ${field}`);
            }

            setFilters(prevFilters => ({
                ...prevFilters,
                [field]: {
                    ...prevFilters[field],
                    options: enhancedOptions,
                    optionsLoading: false
                }
            }));
        } catch (error) {
            props.tabData.updateSpinner(false);
            console.error("Error fetching filter options:", error);
            toast.error("Error loading filter options");
            setFilters(prevFilters => ({
                ...prevFilters,
                [field]: {
                    ...prevFilters[field],
                    options: ['[Empty]', '[Not Empty]'],
                    optionsLoading: false
                }
            }));
        }
    }

    function clearDownstreamFilters(tempFilters, columnName) {
        const currentIndex = filterOrder.indexOf(columnName);
        if (currentIndex === -1) return tempFilters;

        filterOrder.slice(currentIndex + 1).forEach(fieldName => {
            if (tempFilters[fieldName]) {
                tempFilters[fieldName] = {
                    ...tempFilters[fieldName],
                    options: [],
                    hasFilter: false,
                    value: ""
                };
            }
        });
        return tempFilters;
    }

    async function handleFilterChangeAsync(field, value) {
        let tempFilters = ({
            ...filters,
            [field]: {
                ...filters[field],
                value: value === '' ? '' : value
            }
        });

        tempFilters = clearDownstreamFilters(tempFilters, field);

        setFilters(tempFilters);
        updatePageNumber(1);
    }

    function handleDateFilterUiOperatorChange(columnName, uiOperator) {
        setFilters(prevFilters => {
            const previousValue = prevFilters[columnName]?.value || '';
            const value = applyDateFilterUiOperator(uiOperator, previousValue);
            let tempFilters = {
                ...prevFilters,
                [columnName]: {
                    ...prevFilters[columnName],
                    value
                }
            };
            return clearDownstreamFilters(tempFilters, columnName);
        });
        updatePageNumber(1);
    }

    function handleDateFilterDateChange(columnName, operator, dateValue) {
        const date = extractDateFilterInput(dateValue);
        setFilters(prevFilters => {
            const value = date ? serializeDateFilter(operator, date) : '';
            let tempFilters = {
                ...prevFilters,
                [columnName]: {
                    ...prevFilters[columnName],
                    value
                }
            };
            return clearDownstreamFilters(tempFilters, columnName);
        });
        updatePageNumber(1);
    }

    // Open download modal
    function exportToCSV() {
        setIsDownloadModalOpen(true);
    }

    // Export questionnaires (Simple CSV or V20 TSV) with inclusive date range
    async function performExport({ format, fromDate, toDate, completedOnly }: DownloadDataOptions) {
        try {
            if (!company?.id) {
                toast.error("Company information not available");
                return;
            }
            props.tabData.updateSpinner(true);

            // Prepare params for export (no pagination, large limit)
            let params: any = {
                page: 1,
                perPage: 10000, // Large number to get all data
                fromDate,
                toDate,
                completedOnly: completedOnly ? "true" : "false",
            };
            appendLimitToUserSitesParam(params);

            // Format sort columns as a JSON array string, respecting the filter order
            const sortColumns = {};
            filterOrder.forEach(columnName => {
                if (filters[columnName] && filters[columnName].order && filters[columnName].order !== "FILTER_ONLY") {
                    sortColumns[columnName] = filters[columnName].order;
                }
            });

            if (Object.keys(sortColumns).length > 0) {
                params.sortColumns = JSON.stringify(sortColumns);
            }

            // Add field filters if they exist (including special [Empty]/[Not Empty] filters)
            const fieldFilters = Object.entries(filters as any)
                .filter(([_, filterData]: [string, any]) => filterData.value)
                .reduce((acc, [field, filterData]: [string, any]) => {
                    // Pass through special filter values as-is for backend processing
                    acc[field] = filterData.value;
                    return acc;
                }, {} as Record<string, any>);

            if (Object.keys(fieldFilters).length > 0) {
                params.fieldFilters = JSON.stringify(fieldFilters);
            }

            let urlParams = new URLSearchParams(Object.entries(params).reduce((acc, [k, v]) => {
                acc[k] = String(v);
                return acc;
            }, {} as Record<string, string>)).toString();
            let baseUrl = "/Tests/" + company.id;
            let fullUrl = `${baseUrl}?${urlParams}`;
            const resp = await RightTimeApi.getAsync(fullUrl);

            if (!(resp.ok && resp.jsonData && resp.jsonData.tests)) {
                toast.error("Failed to export data");
                return;
            }

            const tests = resp.jsonData.tests;
            if (!tests.length) {
                toast.error("No questionnaires matched the selected filters.");
                return;
            }

            if (format === "v20") {
                const ids = tests
                    .map((t: { id?: number }) => t.id)
                    .filter((id: number | undefined): id is number => typeof id === "number" && id > 0);
                const fileResp = await RightTimeApi.postAsync("/DownloadResults", { ids });
                const ok = await downloadResponseAsFile(fileResp, "TestResults.tsv");
                if (!ok) {
                    toast.error(apiMessage(fileResp, "Failed to download V20 results."));
                    return;
                }
                toast.success(`Downloaded V20 results for ${ids.length} questionnaire(s).`);
                setIsDownloadModalOpen(false);
                return;
            }

            const csvContent = convertToCSV(tests);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const scopeSuffix = completedOnly ? "completed" : "all";
            triggerBrowserDownload(
                blob,
                `questionnaires-export-${fromDate}-to-${toDate}-${scopeSuffix}.csv`
            );
            toast.success(
                `Exported ${tests.length} ${scopeSuffix} records from ${fromDate} to ${toDate}`
            );
            setIsDownloadModalOpen(false);
        } catch (error) {
            console.error("Error exporting questionnaire data:", error);
            toast.error("Error exporting data");
        } finally {
            props.tabData.updateSpinner(false);
        }
    }
    // Convert array of objects to CSV string
    function convertToCSV(data) {
        if (!data || data.length === 0) {
            return '';
        }

        // Get visible columns for CSV headers
        const visibleColumns = columns.filter(col => col.name !== "Download" && col.name !== "StatusEdit");
        const alwaysAppendColumns = [
            { name: "DepartmentName", columnTitle: "Department Name" },
            { name: "Supervisor", columnTitle: "Supervisor" },
            { name: "LanguageTime", columnTitle: "Language Time" },
        ];
        const csvColumns = [
            ...visibleColumns,
            ...alwaysAppendColumns.filter(extra => !visibleColumns.some(col => col.name === extra.name)),
        ];
        const headers = csvColumns.map(col => col.columnTitle || col.name);

        // Create CSV header row
        const csvHeaders = headers.map(header => `"${header.replace(/"/g, '""')}"`).join(',');
        // Helper function to format dates
        const formatDate = (dateValue) => {
            if (!dateValue) return '';
            try {
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) return ''; // Return empty if not a valid date
                // Format as MM/dd/yyyy h:mm AM/PM to match the display format
                const options: Intl.DateTimeFormatOptions = {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                };
                const ret = date.toLocaleDateString('en-US', options); return ret;
            } catch (error) {
                console.error(`Error formatting date ${dateValue}:`, error);
                return ''; // Return empty if parsing fails
            }
        };

        // Create CSV data rows
        const csvRows = data.map(test => {
            return csvColumns.map(col => {
                let value = '';

                // First, try to get the value directly from the test object using various possible field names
                const possibleFieldNames = [
                    col.name,
                    col.name.toLowerCase(),
                    col.name.charAt(0).toLowerCase() + col.name.slice(1), // camelCase version
                ];

                // Find the actual field value
                let fieldValue = null;
                for (const fieldName of possibleFieldNames) {
                    if (test.hasOwnProperty(fieldName)) {
                        fieldValue = test[fieldName];
                        break;
                    }
                }
                // Handle specific known column types - use exact same mappings as RecentsRow.jsx
                switch (col.name) {
                    case 'FirstName':
                        value = test.firstName || '';
                        break;
                    case 'LastName':
                        value = test.lastName || '';
                        break;
                    case 'CompanyName':
                        value = test.companyName || '';
                        break;
                    case 'StatusView':
                        value = test.status || '';
                        break;
                    case 'Job':
                        value = test.job || '';
                        break;
                    case 'Site':
                        value = test.siteName || '';
                        break;
                    case 'Language':
                        value = test.language ? test.language.charAt(0).toUpperCase() + test.language.slice(1).toLowerCase() : '';
                        break;
                    case 'DepartmentName':
                        value = test.departmentName || '';
                        break;
                    case 'Supervisor':
                        value = test.supervisor || '';
                        break;
                    case 'Initials':
                        value = test.initials || '';
                        break;
                    case 'CreateDateTime':
                        value = formatDate(test.created);
                        break;
                    case 'WelcomeDateTime':
                        value = formatDate(test.welcomeTime);
                        break;
                    case 'LanguageTime':
                        value = formatDate(test.languageTime);
                        break;
                    case 'CodeExpiration':
                        value = formatDate(visibleCodeExpiration(test));
                        break;
                    case 'StartDateTime':
                        value = formatDate(test.started);
                        break;
                    case 'FinishDateTime':
                        value = formatDate(test.finished);
                        break;
                    case 'EmbargoDateTime':
                        value = formatDate(test.embargo);
                        break;
                    case 'PulledDateTime':
                        value = formatDate(test.pulled);
                        break;
                    case 'Tags':
                        value = test.tags;
                        break;
                    case 'Duration':
                        if (test.started && test.finished) {
                            if (test.finished <= test.started) {
                                value = "0";
                            } else {
                                // Calculate duration similar to RecentsRow
                                const start = new Date(test.started);
                                const end = new Date(test.finished);
                                const diffMs = end.getTime() - start.getTime();
                                const diffMins = Math.floor(diffMs / 60000);
                                const hours = Math.floor(diffMins / 60);
                                const minutes = diffMins % 60;
                                value = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                            }
                        } else {
                            value = '';
                        }
                        break;
                    case 'TestType':
                        value = test.companyTestTypeName || '';
                        break;
                    case 'TestId':
                        value = test.id ? test.id.toString() : '';
                        break;
                    case 'ApplicantScore':
                        if (test.scores && test.scores.length > 0) {
                            const score1 = test.scores.find(s => s.scoreType === 2);
                            value = (score1 && score1.score) ? score1.score.toString() : '';
                        } else {
                            value = '';
                        }
                        break;
                    case 'CompanyScore':
                        if (test.scores && test.scores.length > 0) {
                            const score2 = test.scores.find(s => s.scoreType === 1);
                            value = (score2 && score2.score) ? score2.score.toString() : '';
                        } else {
                            value = '';
                        }
                        break;
                    case 'OverallScore':
                        if (test.scores && test.scores.length > 0) {
                            const score3 = test.scores.find(s => s.scoreType === 3);
                            value = (score3 && score3.score) ? score3.score.toString() : '';
                        } else {
                            value = '';
                        }
                        break;
                    case 'UniqueId':
                        value = test.uniqueId || '';
                        break;
                    case 'Reports':
                        value = test.hasReports || (typeof test.reportCount === 'number' && test.reportCount > 0) ? 'Available' : '';
                        break;
                    default:
                        // For any unmapped fields, try the original approach
                        value = fieldValue !== null && fieldValue !== undefined ? fieldValue.toString() : '';
                        break;
                }
                // Escape quotes and wrap in quotes if the value contains commas, quotes, or newlines
                if (typeof value === 'string') {
                    value = value.replace(/"/g, '""');
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                        value = `"${value}"`;
                    }
                }

                return value;
            }).join(',');
        });

        return [csvHeaders, ...csvRows].join('\n');
    }

    async function getTestsAsync(refresh = false, includeScores = null) {
        const resolvedCompanyId = getCompanyId();
        if (!resolvedCompanyId) return null;

        props.tabData.updateSpinner(true);
        try {
            return await fetchTestsPageAsync(resolvedCompanyId, refresh, includeScores);
        } finally {
            props.tabData.updateSpinner(false);
        }
    }

    function DetermineShownColumns() {
        if (
            recentsData &&
            recentsData.roleHistoryColumns &&
            recentsData.roleHistoryColumns.length > 0
        ) {
            const currentRoleId = parseInt(simRoleId) || recentsData.defaultRoleId;
            const {
                hasRoleSystemAdmin,
                hasRoleReportView,
                hasRoleMatchView,
                hasRoleTestEdit,
                activeRoleIds,
            } = resolveRecentsColumnView(recentsData.roles, {
                isAdmin,
                simRoleId,
                currentRoleId,
            });

            const hasAdminTestEdit = Array.isArray(props.tabData?.adminPerms)
                && props.tabData.adminPerms.some((perm) => {
                    const permValue = typeof perm === "number" ? perm : (perm?.value ?? perm?.permissionId ?? perm?.permId);
                    return Number(permValue) === Enums.ROLE_PERMS.TestEdit;
                });

            setCanViewReports(isAdmin || hasRoleSystemAdmin || hasRoleReportView);
            setCanViewMatches(isAdmin || hasRoleSystemAdmin || hasRoleMatchView);
            setCanToggleArchived(isAdmin || hasRoleSystemAdmin || hasRoleTestEdit || hasAdminTestEdit);

            updateColumns(buildVisibleRecentsColumns(
                recentsData.roleHistoryColumns,
                activeRoleIds,
                hasRoleSystemAdmin || hasRoleReportView
            ));
        }
    }

    function nextPage() {
        if (pageNumber < totalPages.current) updatePageNumber(pageNumber + 1);
    }

    function prevPage() {
        if (pageNumber > 1) updatePageNumber(pageNumber - 1);
    }

    function firstPage() {
        updatePageNumber(1);
    }

    function lastPage() {
        updatePageNumber(totalPages.current);
    }

    function onToggleTest(testId) {
        if (checked.find((e) => e === testId))
            updateChecks(checked.filter((e) => e !== testId));
        else updateChecks([...checked, testId]);
    } function onClickHeader(e) {
        let column = e.target.getAttribute("data-column");
        if (!e.target.classList.contains("sortable")) return;
        updatePageNumber(1);

        // Find the column object by name
        const columnObj = columns.find(col => col.name === column);
        if (columnObj) {
            clickColumnTitleAsync(columnObj);
        }
    }

    function onChangePerPage(e) {
        const count = parseInt(e.target.value);
        updatePageSize(count);
        localStorage.setItem('perPage', count.toString());
    }

    function editTests(e) {
        if (checked.length > 0) setIsModalOpen(true);
    }

    function ColumnTitle(props) {
        if (props.column.name === "Download") {
            return (
                <th className={props.column.dataType}>
                    <GoGear
                        onClick={(e) => editTests(e)}
                        data-help-id="test-gear-icon"
                        className={`gearActionIcon actionIcon center${checked.length === 0 ? " disabled" : "" }`}
                    />
                </th>
            );
        } else {
            if (props?.column) {
                // Check if this column has sort/filter data
                const columnData = filters[props.column.name];
                const hasSortOrder = columnData && columnData.order;

                return (
                    <th
                        className={`${props.column.dataType} ${hasSortOrder ? 'sorted-column' : ''}`}
                        onClick={(e) => {
                            clickColumnTitleAsync(props.column);
                        }}
                    >
                        {t(props.column.columnTitle || props.column.name)}            {hasSortOrder && (
                            <span className="sort-indicator">
                                {columnData.order === "ASC" ? " ▲" :
                                    columnData.order === "DESC" ? " ▼" :
                                        (columnData.order === "FILTER_ONLY" && columnData.options && columnData.options.length > 0) ? " ▶" : ""}
                                {filterOrder.filter(name => filters[name] && filters[name].order && filters[name].order !== "FILTER_ONLY").length > 1 &&
                                    <sup>{filterOrder.filter(name => filters[name] && filters[name].order && filters[name].order !== "FILTER_ONLY").indexOf(props.column.name) + 1}</sup>
                                }
                            </span>
                        )}
                    </th>
                );
            } else {
                return null;
            }
        }
    }

    function onChangeRole(e) {
        let newRoleId = parseInt(e.currentTarget.value);
        if (newRoleId) {
            updateSimRoleId(newRoleId.toString());
            updatePageNumber(1); // Reset to first page to trigger effect
        }
    }

    async function onSetStatusAsync(e) {
        const testId = parseInt(e.target.getAttribute("data-testId"));
        const testInstanceId = parseInt(e.target.getAttribute("data-testInstanceId"));
        const data = {
            testId: testId,
            testInstanceId: testInstanceId,
            newStatusId: parseInt(e.target.value),
        };
        props.tabData.updateSpinner(true);
        const resp = await RightTimeApi.putAsync("/TestStatus", data);
        props.tabData.updateSpinner(false);
        if (resp && resp.ok) {
            toast.success("Test status updated.");
            await getTestsAsync(true);
        }
    }

    async function onToggleArchivedAsync(e) {
        e.stopPropagation();
        if (!canToggleArchived) {
            toast.error("You need Edit Test permission to change the archive flag.");
            return;
        }
        const row = e.currentTarget.closest("tr");
        const testId = parseInt(e.currentTarget.getAttribute("data-testid") || row?.getAttribute("data-testid"), 10);
        if (!testId) return;
        const test = data?.tests?.find((t) => t.id === testId);
        const changeName = test?.archived ? "unarchive" : "archive";
        props.tabData.updateSpinner(true);
        const resp = await RightTimeApi.putAsync("/Tests", {
            testIds: [testId],
            changes: [{ name: changeName, value: true }],
        });
        props.tabData.updateSpinner(false);
        if (resp && resp.ok) {
            toast.success(test?.archived ? "Test unarchived." : "Test archived.");
            await getTestsAsync(true);
        } else {
            toast.error(resp?.jsonData?.message || "Failed to update archive flag.");
        }
    }

    async function onDownloadV20() {
        const path = `/DownloadResults?${checked.map((n) => `id=${n}`).join("&")}`;
        const resp = await RightTimeApi.getAsync(path);
        const ok = await downloadResponseAsFile(resp, "TestResults.tsv");
        if (!ok) toast.error(apiMessage(resp, "Failed to download V20 results."));
        else toast.success("Downloaded V20 results.");
    }

    async function onDownloadV40(testIds) {
        const tests = data?.tests || [];
        const items = [];

        for (const testId of testIds) {
            const test = tests.find((t) => t.id === testId);
            if (!test?.testInstanceId) {
                toast.error(`Test instance not found for test ${testId}.`);
                continue;
            }

            items.push({
                testId,
                apiPathOrUrl: `/DownloadResults40?id=${testId}`,
                fallbackFilename: `Test-${testId}-${test.testInstanceId}.tsv`,
            });
        }

        if (items.length === 0) return;

        // Single selection: keep the normal browser download (Save As / Downloads folder).
        if (items.length === 1) {
            const item = items[0];
            const ok = await downloadAuthenticatedFile(item.apiPathOrUrl, item.fallbackFilename);
            if (!ok) toast.error(`Failed to download V40 data for test ${item.testId}.`);
            else toast.success("Downloaded 1 V40 data file.");
            return;
        }

        // Multi selection: one folder picker, then write every file into that folder.
        const result = await downloadAuthenticatedFilesToDirectory(items, {
            onItemFailed: (index) => {
                toast.error(`Failed to download V40 data for test ${items[index].testId}.`);
            },
        });

        if (result.cancelled) return;

        if (result.unsupported) {
            // File System Access API unavailable — fall back to per-file browser downloads.
            let downloadedCount = 0;
            for (const item of items) {
                const ok = await downloadAuthenticatedFile(item.apiPathOrUrl, item.fallbackFilename);
                if (!ok) {
                    toast.error(`Failed to download V40 data for test ${item.testId}.`);
                    continue;
                }
                downloadedCount += 1;
            }
            if (downloadedCount > 0) {
                toast.success(`Downloaded ${downloadedCount} V40 data files.`);
            }
            return;
        }

        if (result.downloadedCount > 0) {
            toast.success(`Downloaded ${result.downloadedCount} V40 data files.`);
        }
    }

    async function onDownloadInfo(items) {
        const path = `/DownloadInfo?${items.map((n) => `id=${n}`).join("&")}`;
        const ok = await downloadAuthenticatedFile(path, "TestInfo.tsv");
        if (!ok) toast.error("Failed to download test info.");
        else toast.success("Downloaded test info.");
    }

    function onCloseTestInfoModal() {
        updateDetailsTestInstanceId(null);
        updateDetailsActiveTab("details");
        // Force refresh by clearing the URL cache before calling getTestsAsync
        lastUrl.current = "";
        getTestsAsync();
    }

    if (recentsData) {
        return (
            <>
                {isAdmin && simRoleId ? (
                    <div className="adminHeader">
                        <label htmlFor="roleSelect">View as Role</label>
                        <select id="roleSelect" value={simRoleId} onChange={onChangeRole}>
                            {recentsData.roles
                                .filter((role, index, self) =>
                                    index === self.findIndex(r => r.id === role.id)
                                )
                                .map((r) => (
                                    <option key={"role" + r.id} value={String(r.id)}>
                                        {r.name}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                ) : null}

                <div className="pageContent recentsPage">
                    <div className="tableWrapper dimensionTable recentsTable scrollable">
                        <table className="adminTestList">
                            <caption>
                                <span className="leftHeader sortFilters">
                                    {isAdmin && (
                                        <FaFileExcel
                                            className="export-icon"
                                            onClick={exportToCSV}
                                            title="Export table data"
                                            data-help-id="export-test-data"
                                            size={24}
                                        />)}
                                    {filterOrder.length > 0 ? (
                                        <>
                                            <span className="sort-label">Sort: </span>
                                            <span className="sort-info">
                                                {filterOrder.filter(columnName => filters[columnName]).map((columnName) => {
                                                    const filterData = filters[columnName];
                                                    const columnMeta = columns.find(col => col.name === columnName);
                                                    const isDate = isDateColumn(columnMeta);
                                                    const parsedDateFilter = parseDateFilterValue(filterData.value || '');
                                                    const dateUiOperator = getDateFilterUiOperator(filterData.value || '');
                                                    const dateValue = parsedDateFilter.mode === 'date' ? parsedDateFilter.date : '';
                                                    return (
                                                        <span
                                                            key={`sort-${columnName}`}
                                                            className="sort-item"
                                                            draggable
                                                            onDragStart={(e) => handleFilterDragStart(e, columnName)}
                                                            onDragEnd={handleFilterDragEnd}
                                                            onDragOver={handleFilterDragOver}
                                                            onDrop={(e) => handleFilterDrop(e, columnName)}
                                                        >
                                                            <span
                                                                className="sort-item-content"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleSortDirection(columnName);
                                                                }} title={(() => {
                                                                    if (isSortOnlyColumn(columnName)) {
                                                                        return "Click to toggle sort: ASC ▲ ⇄ DESC ▼";
                                                                    }
                                                                    return "Click to cycle sort: ASC ▲ → DESC ▼ → Filter Only ▶";
                                                                })()}
                                                            >
                                                                {columnMeta?.columnTitle || columnName}
                                                                {(filterData.order && filterData.order !== "FILTER_ONLY") && (
                                                                    <span className="sort-direction sortIcon">
                                                                        {filterData.order === "ASC" ? "▲" : filterData.order === "DESC" ? "▼" : ""}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            {filterData.hasFilter && isDate && (
                                                                <span className="date-filter-controls" onClick={(e) => e.stopPropagation()}>
                                                                    <select
                                                                        className="filter-select date-operator-select"
                                                                        value={dateUiOperator}
                                                                        title={`${columnMeta?.columnTitle || columnName} date filter`}
                                                                        aria-label={`${columnMeta?.columnTitle || columnName} date filter`}
                                                                        onChange={(e) => handleDateFilterUiOperatorChange(columnName, e.target.value)}
                                                                    >
                                                                        <option value="before">Before</option>
                                                                        <option value="after">After</option>
                                                                        <option value="present">Present</option>
                                                                        <option value="empty">Empty</option>
                                                                    </select>
                                                                    {dateFilterNeedsPicker(dateUiOperator) && (
                                                                        <DateTimePicker
                                                                            field={`filter-${columnName}`}
                                                                            pickerTitle={columnMeta?.columnTitle || columnName}
                                                                            value={dateValue}
                                                                            onChange={(e) => handleDateFilterDateChange(columnName, dateUiOperator, e)}
                                                                            className="filter-date-picker"
                                                                            dateOnly
                                                                        />
                                                                    )}
                                                                </span>
                                                            )}
                                                            {filterData.hasFilter && !isDate && isTextFilterColumn(columnName) && (
                                                                <FilterTextInput
                                                                    id={`filter-text-${columnName}`}
                                                                    columnName={columnName}
                                                                    value={filterData.value || ''}
                                                                    title={`Filter ${columnMeta?.columnTitle || columnName}`}
                                                                    ariaLabel={`Filter ${columnMeta?.columnTitle || columnName}`}
                                                                    placeholder="Type Test Id…"
                                                                    autoFocus
                                                                    onChange={handleFilterChangeAsync}
                                                                />
                                                            )}
                                                            {filterData.hasFilter && !isDate && !isTextFilterColumn(columnName) && !isSortOnlyColumn(columnName) && (
                                                                <FilterValueSelect
                                                                    id={`filter-select-${columnName}`}
                                                                    columnName={columnName}
                                                                    value={filterData.value || ''}
                                                                    options={filterData.options || []}
                                                                    loading={!!filterData.optionsLoading}
                                                                    title={`Filter ${columnMeta?.columnTitle || columnName}`}
                                                                    ariaLabel={`Filter ${columnMeta?.columnTitle || columnName}`}
                                                                    autoFocus
                                                                    onChange={handleFilterChangeAsync}
                                                                />
                                                            )}
                                                            <span
                                                                className={`filter-icon sortIcon ${filterData.value ? 'active-filter' : ''}`} onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (filterData.hasFilter) {
                                                                        disableColumnFilter(columnName);
                                                                    } else {
                                                                        enableColumnFilter(columnName);
                                                                    }
                                                                }}
                                                                title={
                                                                    filterData.value
                                                                        ? filterData.value === '[Empty]'
                                                                            ? "Filtering for empty values - click to clear"
                                                                            : filterData.value === '[Not Empty]'
                                                                                ? "Filtering for non-empty values - click to clear"
                                                                                : filterData.value.startsWith('[Before]') || filterData.value.startsWith('[After]')
                                                                                    ? `Filtering for dates ${filterData.value.startsWith('[Before]') ? 'before' : 'on or after'} ${filterData.value.replace(/^\[(Before|After)\]/, '')} - click to clear`
                                                                                    : `Filtering for: ${filterData.value} - click to clear`
                                                                        : "Filter values"
                                                                }
                                                            ><FaFilter size={12} />
                                                            </span>

                                                            <span
                                                                className="remove-sort sortIcon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeColumnFilter(columnName);
                                                                }}
                                                                title="Remove this sort criterion"
                                                            >X</span>
                                                        </span>
                                                    );
                                                })}
                                            </span>
                                        </>
                                    ) : null}
                                </span>
                                {t("Recent Questionnaires")} {recentsData.canAccessAdmin && `(${totalCount})`}
                                <div className="captionRight">
                                    <span onClick={firstPage}>
                                        <HiOutlineChevronDoubleLeft className="buttonIcons" />
                                    </span>
                                    <span onClick={prevPage}>
                                        <HiOutlineChevronLeft className="buttonIcons" />
                                    </span>
                                    <span className="pageControlsText">
                                        Page{" "}
                                        <span className={`pageControlsNum digits-${String(totalPages.current).length}`}>
                                            {pageNumber}
                                        </span>
                                        {" "}of{" "}
                                        <span className={`pageControlsNum digits-${String(totalPages.current).length}`}>
                                            {totalPages.current}
                                        </span>
                                    </span>
                                    <span onClick={nextPage}>
                                        <HiOutlineChevronRight className="buttonIcons" />
                                    </span>
                                    <span onClick={lastPage}>
                                        <HiOutlineChevronDoubleRight className="buttonIcons" />
                                    </span>
                                    <span>
                                        <label>Show:</label>
                                        <input
                                            className="perPage"
                                            value={pageSizeStr}
                                            onChange={(e) => updatePageSizeStr(e.target.value)}
                                            onBlur={onChangePerPage}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    (e.target as HTMLInputElement).blur();
                                                }
                                            }}
                                        />
                                    </span>
                                </div>
                            </caption>
                            <thead>
                                <tr onClick={onClickHeader}>
                                    {columns
                                        ? columns.map((rhc) => {
                                            return (
                                                <ColumnTitle column={rhc} sort={filters} key={"col" + rhc.historyColumnId} onClick={clickColumnTitleAsync} />
                                            );
                                        })
                                        : null}
                                </tr>
                            </thead>
                            <tbody>
                                {data.tests ? (
                                    columns && columns.length > 0 ? (
                                        data.tests.map((t, i) => {
                                            return (
                                                <RecentsRow
                                                    test={t}
                                                    company={recentsData}
                                                    i={i}
                                                    toggleRowSelect={onToggleTest}
                                                    onSetStatus={onSetStatusAsync}
                                                    onToggleArchived={canToggleArchived ? onToggleArchivedAsync : undefined}
                                                    canToggleArchived={canToggleArchived}
                                                    checked={checked.indexOf(t.id) >= 0}
                                                    role={simRoleId}
                                                    columns={columns}
                                                    onScoreClick={showTestScores}
                                                    onMatchClick={showTestMatches}
                                                    onClick={showTestDetails}
                                                    onReportClick={props.onReportClick}
                                                    canViewReports={canViewReports}
                                                    canViewMatches={canViewMatches}
                                                    key={"testrow" + t.testInstanceId}
                                                    statuses={recentsData.statuses}
                                                    isAdmin={isAdmin}
                                                />
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={99}>No columns have been configured to display</td>
                                        </tr>)) : (

                                    <tr>
                                        <td colSpan={99}>No Questionnaires to show</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <TestEditModal
                    isOpen={isModalOpen}
                    onClose={onCloseTestEditModalAsync}
                    checkedItems={checked}
                    statuses={statuses}
                    sites={recentsData.sites}
                    onDownload={onDownloadV20}
                    onDownloadV40={onDownloadV40}
                    onDownloadInfo={onDownloadInfo}
                />
                {detailsTestInstanceId && (
                    <TestDetailsModal
                        key={detailsTestInstanceId}
                        onClose={onCloseTestInfoModal}
                        testInstanceId={detailsTestInstanceId}
                        updateSpinner={props.tabData.updateSpinner}
                        isSystemAdmin={props.tabData?.adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin) || false}
                        initialActiveTab={detailsActiveTab}
                        adminPerms={props.tabData?.adminPerms}
                    />
                )}
                <DownloadDataModal
                    isOpen={isDownloadModalOpen}
                    onDownload={performExport}
                    onClose={() => setIsDownloadModalOpen(false)}
                    title="Export Questionnaire Data"
                />
            </>
        );
    } else return null;
};

